import React from "react";
import {
  Grid2 as Grid,
  Button,
  Tabs,
  Tab,
  Stack,
  Typography,
} from "@mui/material";
import { useParams, useNavigate } from "react-router";
import { useTranslate } from "@refinedev/core";

import { DataGrid } from "@shared/components/DataGrid";
import { AgreementProductsController } from "./controller";
import { useAgreementProductsColumns } from "./colluns";
import { usePaginatedTable } from "@shared/hooks";
import { AgreementProductPlan } from "@modules/agreements/types/agreementProduct";
import { ModalRef, TableCell } from "@shared/components";
import { EditButton, DeleteButton } from "@refinedev/mui";
import { EditAgreementProductPlanModal } from "./AgrementProductPlanModal/EditAgreementProductPlanModal";
import { CreateAgreementProductPlanModal } from "./AgrementProductPlanModal/CreateAgreementProductPlanModal";
import { useListLevelsQuery } from "@modules/levels/services/queries";
import { CreateAgreementProductModal } from "./AgrementProductModal/CreateAgreementProductModal";
import { EditAgreementProductModal } from "./AgrementProductModal/EditAgreementProductModal";

export const AgreementProductsModule = () => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const { id: agreementId } = useParams();

  const { rows, tableProps, refetch, createProductModalRef } =
    AgreementProductsController();

  const [tab, setTab] = React.useState<"products" | "plans">("products");
  const [selectedProduct, setSelectedProduct] = React.useState<{
    id: number;
    name: string;
  } | null>(null);
  const createPlanModalRef = React.useRef<ModalRef>(null);
  const editPlanModalRef = React.useRef<ModalRef>(null);
  const [planToEdit, setPlanToEdit] = React.useState<any | null>(null);

  const plansTable = usePaginatedTable<AgreementProductPlan>({
    resource: "v1/subscription_plan_admin",
    enabled: tab === "plans" && !!selectedProduct?.id,
    filters: {
      agreement: agreementId,
      product: selectedProduct?.id,
    },
  });

  const columns = useAgreementProductsColumns({
    onRefetch: refetch,
    onViewPlans: (row) => {
      setSelectedProduct({ id: row.id || 0, name: row.name });
      setTab("plans");
    },
    onView: (row) =>
      navigate(`/agreements/${agreementId}/products/view/${row.id}`),
    onEdit: (row) =>{
      editPlanModalRef.current?.open({
        id: row.id || 0,
        name: row.name,
        active: row.active,
        default_product: row.default_product?.id ?? null,
        metadata: row.metadata,
      })}
  });

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => {
            if (v === "plans" && !selectedProduct?.id) return;
            setTab(v);
          }}
        >
          <Tab
            value="products"
            label={translate("agreement_product_admin.tabs.products") as string}
          />
          <Tab
            value="plans"
            label={translate("agreement_product_admin.tabs.plans") as string}
            disabled={!selectedProduct?.id}
          />
        </Tabs>
      </Grid>

      {tab === "products" && (
        <>
          <Grid size={{ xs: 12 }}>
            <Button
              variant="contained"
              size="medium"
              color="primary"
              onClick={() =>
                createProductModalRef.current?.open({ id: agreementId })
              }
            >
              {translate("v1/agreement_admin.buttons.create_product")}
            </Button>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <DataGrid
              {...tableProps}
              rows={rows}
              columns={columns}
            />
          </Grid>

          <CreateAgreementProductModal
            modalRef={createProductModalRef}
            onComplete={() => {
              refetch()
            }}
          />

          <EditAgreementProductModal
            modalRef={editPlanModalRef}
            onComplete={() => {
              refetch()
            }}
          />
        </>
      )}

      {tab === "plans" && (
        <>
          <Grid size={{ xs: 12 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="h6">
                {translate("agreement_product_admin.plans_of")}{" "}
                {selectedProduct?.name}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" onClick={() => setTab("products")}>
                  {translate("buttons.back")}
                </Button>
                <Button
                  variant="contained"
                  onClick={() => createPlanModalRef.current?.open({})}
                >
                  {translate("subscription_plan_admin.titles.create")}
                </Button>
              </Stack>
            </Stack>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <DataGrid
              rows={plansTable.rows}
              columns={[
                {
                  field: "id",
                  headerName: translate("subscription_plan_admin.fields.id"),
                  minWidth: 100,
                },
                {
                  field: "name",
                  headerName: translate("subscription_plan_admin.fields.name"),
                  flex: 1,
                  minWidth: 200,
                },
                {
                  field: "value",
                  headerName: translate("subscription_plan_admin.fields.value"),
                  minWidth: 120,
                },
                {
                  field: "level",
                  headerName: translate("subscription_plan_admin.fields.level"),
                  minWidth: 120,
                  renderCell: ({ value }) => (
                    <TableCell.Fetch
                      align="center"
                      resourceHook={useListLevelsQuery}
                      id={value}
                      valueKey="name"
                    />
                  ),
                },

                {
                  field: "actions",
                  headerName: translate("table.actions"),
                  headerAlign: "center",
                  minWidth: 260,
                  sortable: false,
                  renderCell: ({ row }) => (
                    <TableCell.Container align="center">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <EditButton hideText onClick={() => {
                          setPlanToEdit(row);
                          editPlanModalRef.current?.open({ id: row.id, ...row });
                        }} />

                        <DeleteButton
                          hideText
                          recordItemId={row.id}
                          resource="v1/subscription_plan_admin"
                          onSuccess={() => plansTable.refetch?.()}
                        />
                      </Stack>
                    </TableCell.Container>
                  ),
                },
              ]}
              rowCount={plansTable.rowCount}
              loading={plansTable.loading}
              paginationModel={plansTable.paginationModel}
              onPaginationModelChange={plansTable.onPaginationModelChange}
              onSortModelChange={plansTable.onSortModelChange}
            />
          </Grid>
        </>
      )}

      <CreateAgreementProductPlanModal
        modalRef={createPlanModalRef}
        agreementId={agreementId as any}
        productId={selectedProduct?.id || null}
        onComplete={() => plansTable.refetch?.()}
      />

      {planToEdit && (
        <EditAgreementProductPlanModal
          modalRef={editPlanModalRef}
          onComplete={() => {
            plansTable.refetch?.();
            setPlanToEdit(null);
          }}
        />
      )}
    </Grid>
  );
};
